
(() => {
  if (!('customElements' in window) || customElements.get('inline-predictive-search')) {
    return;
  }

  class InlinePredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-inline-search-input]');
      this.panel = this.querySelector('.header__inline-search-panel');
      this.resultsElement = this.querySelector('.header__inline-search-results');
      this.footerElement = this.querySelector('.header__inline-search-footer');
      this.viewAllLink = this.querySelector('[data-inline-search-all]');
      this.searchUrl = (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.searchUrl) || '/search';
      this.abortController = null;

      if (!this.input || !this.panel) {
        return;
      }

      this._onSearchDebounced = this._debounce(this._onSearch.bind(this), 200);
      this.input.addEventListener('input', this._onSearchDebounced);
      this.input.addEventListener('focus', () => {
        // Only re-open if there is already something to show.
        if (this.input.value.trim() !== '' && this.resultsElement.innerHTML.trim() !== '') {
          this._openPanel();
        }
      });

      this._onDocumentClick = (event) => {
        if (!this.contains(event.target)) {
          this._closePanel();
        }
      };
      document.addEventListener('click', this._onDocumentClick);

      // "New search" button inside the no-results state.
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-action="reset-search"]')) {
          this.input.value = '';
          this.resultsElement.innerHTML = '';
          this._closePanel();
          this.input.focus();
        }
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this._closePanel();
          this.input.blur();
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocumentClick);
    }

    async _onSearch(event) {
      if (event && event.key === 'Enter') {
        return;
      }

      const term = this.input.value.trim();

      if (this.abortController) {
        this.abortController.abort();
      }

      // Empty input: hide the panel entirely, no loader, no leftover results.
      if (term === '') {
        this.resultsElement.innerHTML = '';
        if (this.footerElement) this.footerElement.hidden = true;
        this._closePanel();
        return;
      }

      let searchResults = { html: '', hasResults: false };

      try {
        this.abortController = new AbortController();
        searchResults = await this._fetchResults(term);
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
      }

      this.resultsElement.innerHTML = searchResults.html;

      // Footer "view all results" only when there are products to view.
      if (this.footerElement) {
        this.footerElement.hidden = !searchResults.hasResults;
      }
      if (this.viewAllLink) {
        this.viewAllLink.href = `${this.searchUrl}?q=${encodeURIComponent(term)}&type=product`;
      }

      this._openPanel();
    }

    async _fetchResults(term) {
      const routes = (window.themeVariables && window.themeVariables.routes) || {};
      const usePredictive = this._supportsPredictiveApi();
      const base = usePredictive ? routes.predictiveSearchUrl : routes.searchUrl;
      const sectionId = usePredictive ? 'predictive-search' : 'predictive-search-compatibility';
      // Products only.
      const limit = usePredictive
        ? '&resources[type]=product&resources[limit]=10&resources[limit_scope]=each'
        : '&type=product&resources[limit]=50';

      const response = await fetch(
        `${base}?q=${encodeURIComponent(term)}${limit}&section_id=${sectionId}`,
        { signal: this.abortController.signal }
      );

      const div = document.createElement('div');
      div.innerHTML = await response.text();

      return {
        hasResults: div.querySelector('.predictive-search__results-categories') !== null,
        html: div.firstElementChild ? div.firstElementChild.innerHTML : ''
      };
    }

    _supportsPredictiveApi() {
      const featuresElement = document.getElementById('shopify-features');

      if (!featuresElement) {
        return true;
      }

      try {
        return JSON.parse(featuresElement.textContent).predictiveSearch !== false;
      } catch (error) {
        return true;
      }
    }

    _openPanel() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
    }

    _closePanel() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
    }

    _debounce(fn, delay) {
      let timer = null;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      };
    }
  }

  customElements.define('inline-predictive-search', InlinePredictiveSearch);
})();

/**
 * Crosses out, in every option group of the variant picker, the values that have no variant in stock
 * for the values currently selected in the other groups — selecting "Vanilla Ice Cream" crosses out
 * the sizes that are sold out for that flavor, for instance.
 *
 * The element lives inside <variant-picker>, so it runs again on its own every time the theme
 * re-renders the picker (option change, quick buy drawer...).
 */
(() => {
  if (!('customElements' in window) || customElements.get('variant-availability')) {
    return;
  }

  const SWATCH_SELECTOR = '.block-swatch, .color-swatch, .variant-swatch, .combo-box__option-item';

  class VariantAvailability extends HTMLElement {
    connectedCallback() {
      this.picker = this.closest('variant-picker');
      this.variants = this._parseVariants();

      if (!this.picker || this.variants.length === 0) {
        return;
      }

      // Cross out right away on click, without waiting for the section to be re-rendered.
      this._onChange = (event) => {
        if (event.target.matches('[data-option-position]')) {
          this.update();
        }
      };

      this.picker.addEventListener('change', this._onChange);
      this.update();
    }

    disconnectedCallback() {
      if (this.picker && this._onChange) {
        this.picker.removeEventListener('change', this._onChange);
      }
    }

    update() {
      const selectedValues = this._selectedValues();

      this.picker.querySelectorAll('[data-option-position][data-option-value]').forEach((element) => {
        // Hidden inputs only carry the current selection, they have nothing to cross out.
        if (element.type === 'hidden') {
          return;
        }

        const position = parseInt(element.getAttribute('data-option-position'), 10);
        const available = this._hasAvailableVariant(position, element.getAttribute('data-option-value'), selectedValues);

        (element.closest(SWATCH_SELECTOR) || element).classList.toggle('is-disabled', !available);
      });
    }

    /**
     * The value currently selected in each option group, keyed by option position.
     */
    _selectedValues() {
      const values = {};

      this.picker
        .querySelectorAll('input[data-option-value]:checked, input[data-option-value][type="hidden"]')
        .forEach((input) => {
          values[parseInt(input.getAttribute('data-option-position'), 10)] = input.getAttribute('data-option-value');
        });

      return values;
    }

    /**
     * Whether at least one variant in stock has `value` at `position` while matching the values
     * currently selected in all the other option groups.
     */
    _hasAvailableVariant(position, value, selectedValues) {
      return this.variants.some((variant) => {
        return (
          variant.available &&
          variant.options.every((optionValue, index) => {
            const optionPosition = index + 1;

            if (optionPosition === position) {
              return optionValue === value;
            }

            return !(optionPosition in selectedValues) || selectedValues[optionPosition] === optionValue;
          })
        );
      });
    }

    _parseVariants() {
      try {
        return JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      } catch (error) {
        return [];
      }
    }
  }

  customElements.define('variant-availability', VariantAvailability);
})();